/**
 * Agreement API hooks — TanStack Query wrappers for agreement management.
 * Mirrors useRedeemGroupApi patterns: typed hooks, toast feedback on mutations,
 * keyed cache invalidation.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authenticatedApi } from '../lib/authenticated-api';
import type {
  Agreement,
  AgreementVersion,
  AgreementWithBody,
  CreateAgreementInput,
  ListAgreementsResponse,
  ListUserResponsesResponse,
  ListVersionsResponse,
  SubmitAgreementResponseInput,
  UpdateAgreementBodyInput,
  UpdateAgreementMetaInput,
  UserAgreementHistoryListResponse,
  UserAgreementResponse,
} from '../lib/types/agreement';

// Cache keys
export const agreementsQueryKeys = {
  all: ['agreements'] as const,
  adminList: (includeDisabled?: boolean) =>
    ['agreements', 'admin-list', { includeDisabled }] as const,
  adminDetail: (slug: string) => ['agreements', 'admin-detail', slug] as const,
  publicDetail: (slug: string) =>
    ['agreements', 'public-detail', slug] as const,
  versions: (slug: string) => ['agreements', 'versions', slug] as const,
  version: (slug: string, version: number) =>
    ['agreements', 'version', slug, version] as const,
  userResponses: (uid: string) => ['agreements', 'user', uid] as const,
  userResponse: (uid: string, slug: string) =>
    ['agreements', 'user', uid, slug] as const,
  userHistory: (uid: string, slug: string) =>
    ['agreements', 'user-history', uid, slug] as const,
};

function extractMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    if ('response' in error) {
      const response = (error as { response?: { data?: { message?: string } } })
        .response;
      if (response?.data?.message) return response.data.message;
    }
    if (
      'message' in error &&
      typeof (error as { message: unknown }).message === 'string'
    ) {
      return (error as { message: string }).message;
    }
  }
  return fallback;
}

// -------------- admin list/detail --------------

export function useAgreementsList(includeDisabled = true) {
  return useQuery<ListAgreementsResponse>({
    queryKey: agreementsQueryKeys.adminList(includeDisabled),
    queryFn: async () => {
      const response = await authenticatedApi.listAgreements({
        includeDisabled,
      });
      if (!response.data) throw new Error('No data returned');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAgreement(slug: string | undefined) {
  return useQuery<AgreementWithBody>({
    queryKey: agreementsQueryKeys.adminDetail(slug || ''),
    queryFn: async () => {
      const response = await authenticatedApi.getAgreementAdmin(slug!);
      if (!response.data) throw new Error('No data returned');
      return response.data;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAgreementVersions(slug: string | undefined) {
  return useQuery<ListVersionsResponse>({
    queryKey: agreementsQueryKeys.versions(slug || ''),
    queryFn: async () => {
      const response = await authenticatedApi.listAgreementVersions(slug!);
      if (!response.data) throw new Error('No data returned');
      return response.data;
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAgreementVersion(
  slug: string | undefined,
  version: number | undefined
) {
  return useQuery<AgreementVersion>({
    queryKey: agreementsQueryKeys.version(slug || '', version || 0),
    queryFn: async () => {
      const response = await authenticatedApi.getAgreementVersion(
        slug!,
        version!
      );
      if (!response.data) throw new Error('No data returned');
      return response.data;
    },
    enabled: !!slug && !!version,
    staleTime: 5 * 60 * 1000,
  });
}

// -------------- admin mutations --------------

export function useCreateAgreement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAgreementInput) => {
      const response = await authenticatedApi.createAgreement(input);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.all,
      });
      toast.success('Agreement created');
    },
    onError: err => {
      toast.error(extractMessage(err, 'Failed to create agreement'));
    },
  });
}

export function useUpdateAgreementBody() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      slug,
      input,
    }: {
      slug: string;
      input: UpdateAgreementBodyInput;
    }) => {
      const response = await authenticatedApi.updateAgreementBody(slug, input);
      return response.data;
    },
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.adminDetail(slug),
      });
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.versions(slug),
      });
      toast.success('New version published');
    },
    onError: err => {
      toast.error(extractMessage(err, 'Failed to publish new version'));
    },
  });
}

export function useUpdateAgreementMeta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      slug,
      input,
    }: {
      slug: string;
      input: UpdateAgreementMetaInput;
    }) => {
      const response = await authenticatedApi.updateAgreementMeta(slug, input);
      return response.data;
    },
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.adminDetail(slug),
      });
      toast.success('Agreement updated');
    },
    onError: err => {
      toast.error(extractMessage(err, 'Failed to update agreement'));
    },
  });
}

// -------------- user-facing --------------

export function useUserAgreements(uid: string | undefined) {
  return useQuery<ListUserResponsesResponse>({
    queryKey: agreementsQueryKeys.userResponses(uid || ''),
    queryFn: async () => {
      const response = await authenticatedApi.listUserAgreements(uid!);
      if (!response.data) throw new Error('No data returned');
      return response.data;
    },
    enabled: !!uid,
    staleTime: 60 * 1000,
  });
}

export function useUserAgreement(
  uid: string | undefined,
  slug: string | undefined
) {
  return useQuery<UserAgreementResponse>({
    queryKey: agreementsQueryKeys.userResponse(uid || '', slug || ''),
    queryFn: async () => {
      const response = await authenticatedApi.getUserAgreement(uid!, slug!);
      if (!response.data) throw new Error('No data returned');
      return response.data;
    },
    enabled: !!uid && !!slug,
    staleTime: 60 * 1000,
  });
}

export function useUserAgreementHistory(
  uid: string | undefined,
  slug: string | undefined,
  limit?: number
) {
  return useQuery<UserAgreementHistoryListResponse>({
    queryKey: agreementsQueryKeys.userHistory(uid || '', slug || ''),
    queryFn: async () => {
      const response = await authenticatedApi.getUserAgreementHistory(
        uid!,
        slug!,
        limit ? { limit } : undefined
      );
      if (!response.data) throw new Error('No data returned');
      return response.data;
    },
    enabled: !!uid && !!slug,
    staleTime: 60 * 1000,
  });
}

export function useSubmitUserAgreementResponse() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      uid,
      slug,
      input,
    }: {
      uid: string;
      slug: string;
      input: SubmitAgreementResponseInput;
    }) => {
      const response = await authenticatedApi.submitUserAgreementResponse(
        uid,
        slug,
        input
      );
      return response.data;
    },
    onSuccess: (_data, { uid, slug }) => {
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.userResponses(uid),
      });
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.userResponse(uid, slug),
      });
      queryClient.invalidateQueries({
        queryKey: agreementsQueryKeys.userHistory(uid, slug),
      });
    },
    onError: err => {
      toast.error(extractMessage(err, 'Failed to submit response'));
    },
  });
}

export type { Agreement };
